import AttributionDetails from './AttributionDetails';
import { SecurityIdentityFieldMenu, securityIdentityFieldLabel } from '../components/commonComponents/SecurityIdentityFieldSelector';
import { securityIdentityPresentation } from '../utils/securityIdentityPresentation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Popover,
    Portal,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import SouthEastRoundedIcon from '@mui/icons-material/SouthEastRounded';
import SouthWestRoundedIcon from '@mui/icons-material/SouthWestRounded';
import { AlertTriangle } from 'lucide-react';
import DataTable from '../components/data_table/DataTable';
import { sortByNumber, sortByText } from '../components/data_table/sorting';
import KitIconButton from '../components/ui/IconButton';
import KitTooltip from '../components/ui/Tooltip';
import { cx } from '../components/ui/classNames';
import {
    performanceInsetSx,
    performancePopoverPaperSx,
    performanceSecondaryControlSx,
    performanceToggleButtonSx,
    performanceToggleGroupSx,
} from '../styles/performanceMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
} from '../styles/msiStyleTokens';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const MIN_WIDTH = 320;
const MIN_HEIGHT = 340;
const VIEWPORT_PADDING = 16;
const ELEVATED_CARD_SX = {
    position: 'relative',
    borderRadius: '6px',
    borderTop: '1px solid rgba(226, 232, 240, 0.98)',
    borderLeft: '1px solid rgba(226, 232, 240, 0.98)',
    borderRight: '1px solid rgba(148, 163, 184, 0.56)',
    borderBottom: '3px solid rgba(100, 116, 139, 0.34)',
    boxShadow:
        'inset 0 1px 0 rgba(255, 255, 255, 0.92), 4px 4px 0 rgba(30, 41, 59, 0.44), 4px 4px 0 1px rgba(148, 163, 184, 0.16), 0 12px 20px rgba(0, 0, 0, 0.22)',
};

const CORE_COMPONENT_COLUMNS = [
    { key: 'total', label: 'Total' },
    { key: 'factor', label: 'Factor' },
    { key: 'idio', label: 'Idio' },
    { key: 'trading', label: 'Trading' },
    { key: 'non_trading', label: 'Non-Trading' },
    { key: 'realized', label: 'Realized' },
    { key: 'unrealized', label: 'Unrealized' },
];

const TAB_SECURITY_RETURNS = 'security_returns';
const TAB_RECENT_TRADES = 'recent_trades';
const EMPTY_IDENTITY_ROWS = Object.freeze({});

// The security returns heat map: the identity column keeps its 190px (the
// old grid's minimum, which it rarely grew past) while the component
// columns share the rest, at least 130px each. Opens by security name; a
// header click flips its column between directions.
const BREAKOUT_IDENTITY_WIDTH = 190;
const INITIAL_BREAKOUT_SORTING = Object.freeze([{ id: 'security', desc: false }]);
// A recent trade inside the selected range or at the selected point.
const IN_RANGE_TRADE_ROW_PROPS = Object.freeze({ className: '!bg-ui-warning-bg', 'data-in-range': '' });
// Every heat-map value is bold; the Total row (under the header) is extra
// bold on the selected tint, with the accent on its first cell.
const breakoutCellClassName = ({ row }) => (row.original.total ? '!bg-ui-selected font-extrabold' : 'font-bold');
const breakoutIdentityClassName = (context) => cx(
    breakoutCellClassName(context),
    context.row.original.total && 'shadow-[inset_3px_0_theme(colors.tbl.accent)]',
);
const getBreakoutRowId = (row) => row.instrumentKey;
const getTradeRowId = (row) => [
    row?.event_id || '',
    row?.execution_id || '',
    row?.executed_at || row?.executed_at_label || '',
    row?.instrument_key || '',
].join(':');

const COVERAGE_REASON_DETAILS = {
    zero_prices_found_for_selected_day: 'No prices were found for this security on the selected day.',
    limited_price_coverage: 'Only limited prices were found for this security on the selected day. Returns may be slightly inaccurate at some times.',
    missing_price_coverage: 'Price data was incomplete for one or more required marks in this window.',
    no_price_coverage: 'No price data was found for this security in the selected window.',
    insufficient_cost_basis_history: 'Realized PnL could not be fully computed because cost-basis history is incomplete.',
    missing_trade_price: 'One or more trades were missing trade price data.',
    missing_watchlist_mapping: 'This security is not mapped in your current watchlist.',
    factor_data_imputed: 'Verified factor data is unavailable. Estimated inputs are not used in these returns.',
    not_in_watchlist: 'This security is not in your watchlist and was excluded from calculations.',
};


function formatCellValue(value, valueMode) {
    if (value == null || Number.isNaN(Number(value))) return '-';
    const n = Number(value);
    if (valueMode === 'percent') {
        return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(2)}%`;
    }
    return n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
    });
}

function formatSummaryMetric(metricKey, value) {
    if (value == null || Number.isNaN(Number(value))) return '-';
    const n = Number(value);
    if (metricKey === 'hit_rate') return `${(n * 100).toFixed(0)}%`;
    return n.toFixed(2);
}

function resolveHeatColor(value, maxAbs) {
    const n = Number(value || 0);
    const denom = maxAbs > 0 ? maxAbs : 1;
    const intensity = Math.min(Math.abs(n) / denom, 1);
    const alpha = 0.12 + (0.5 * intensity);
    if (n > 0) return `rgba(34, 197, 94, ${alpha.toFixed(3)})`;
    if (n < 0) return `rgba(239, 68, 68, ${alpha.toFixed(3)})`;
    return 'rgba(148, 163, 184, 0.10)';
}

function normalizeSortValue(value) {
    if (value == null) return 0;
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return num;
}

function formatCoverageReasonDetail(reason) {
    const key = String(reason || '').trim();
    if (!key) return 'Coverage details are unavailable.';
    return COVERAGE_REASON_DETAILS[key] || key.replace(/_/g, ' ');
}

function normalizeDateOnly(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const match = raw.match(/\d{4}-\d{2}-\d{2}/);
    if (match) return match[0];
    return raw;
}

function formatTimeOnly(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    }
    const isoMatch = raw.match(/T(\d{2}:\d{2})/);
    if (isoMatch) return isoMatch[1];
    const timeMatch = raw.match(/\b(\d{2}:\d{2})(?::\d{2})?\b/);
    if (timeMatch) return timeMatch[1];
    return raw;
}

function viewportLimits() {
    const width = Math.max(1, window.innerWidth - (VIEWPORT_PADDING * 2));
    const height = Math.max(1, window.innerHeight - (VIEWPORT_PADDING * 2));
    return { width, height, minWidth: Math.min(MIN_WIDTH, width), minHeight: Math.min(MIN_HEIGHT, height) };
}

function initialPanelSize() {
    const limits = viewportLimits();
    return { width: Math.min(900, limits.width), height: Math.min(650, limits.height) };
}

export default function SecurityBreakoutPopout({
    open,
    onClose,
    securityBreakout,
    seriesMode = 'cumulative',
    loading = false,
    groupLabel = null,
    windowStartDate = null,
    windowEndDate = null,
    asOfDate = null,
    selectedTimestamp = null,
    selectedRangeStart = null,
    selectedRangeEnd = null,
    executionContext = null,
    summaryMetrics = null,
    attributionMode = false,
    attributionDecompositionMode = 'risk',
    analysisSource = null,
    analysisBasis = null,
    attributionDetail = null,
    attributionDetailError = null,
    factorDetailFocus = null,
    identityRows = EMPTY_IDENTITY_ROWS,
    identityDisplayField = null,
    onIdentityDisplayFieldChange = null,
    reportingCurrency = null,
    selectedFactors = [],
    attributionAnalysisBasis = null,
}) {
    const [localIdentityField, setLocalIdentityField] = useState('company_name');
    const identityField = identityDisplayField ?? localIdentityField;
    const changeIdentityField = onIdentityDisplayFieldChange ?? setLocalIdentityField;
    const identityFor = useCallback((item) => {
        const explicit = typeof item?.cognive_id === 'string' ? item.cognive_id.trim() : '';
        const key = String(item?.instrument_key || '');
        const cogniveId = explicit || (key.startsWith('COGNIVE:') ? key.slice('COGNIVE:'.length) : '');
        return securityIdentityPresentation(identityRows[cogniveId] || { cognive_id: cogniveId }, identityField);
    }, [identityRows, identityField]);
    const identityHeaderControls = useCallback(
        () => <SecurityIdentityFieldMenu value={identityField} onChange={changeIdentityField} />,
        [identityField, changeIdentityField],
    );
    const panelRef = useRef(null);
    const dragStateRef = useRef(null);
    const resizeStateRef = useRef(null);
    const sizeRef = useRef({ width: 900, height: 650 });
    const positionRef = useRef({ x: 20, y: 40 });
    const restoredLayoutRef = useRef(null);
    const [position, setPosition] = useState({ x: 20, y: 40 });
    const [size, setSize] = useState({ width: 900, height: 650 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [valueMode, setValueMode] = useState('dollar');
    const [activeTab, setActiveTab] = useState(TAB_SECURITY_RETURNS);
    const [sorting, setSorting] = useState(INITIAL_BREAKOUT_SORTING);
    const [coveragePopover, setCoveragePopover] = useState({ anchorEl: null, row: null });

    const applyLayout = useCallback((nextPosition, nextSize) => {
        const limits = viewportLimits();
        const boundedSize = {
            width: clamp(nextSize.width, limits.minWidth, limits.width),
            height: clamp(nextSize.height, limits.minHeight, limits.height),
        };
        const boundedPosition = {
            x: clamp(nextPosition.x, VIEWPORT_PADDING, window.innerWidth - boundedSize.width - VIEWPORT_PADDING),
            y: clamp(nextPosition.y, VIEWPORT_PADDING, window.innerHeight - boundedSize.height - VIEWPORT_PADDING),
        };
        sizeRef.current = boundedSize;
        positionRef.current = boundedPosition;
        setSize(boundedSize);
        setPosition(boundedPosition);
    }, []);

    const endPointerInteraction = useCallback(() => {
        dragStateRef.current = null;
        resizeStateRef.current = null;
        setIsDragging(false);
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (!open) {
            endPointerInteraction();
            setCoveragePopover({ anchorEl: null, row: null });
            return undefined;
        }
        const frameId = window.requestAnimationFrame(() => {
            const nextSize = initialPanelSize();
            restoredLayoutRef.current = null;
            setExpanded(false);
            applyLayout({ x: window.innerWidth - nextSize.width - 24, y: 80 }, nextSize);
        });
        const handleViewportResize = () => {
            applyLayout(
                restoredLayoutRef.current ? { x: VIEWPORT_PADDING, y: VIEWPORT_PADDING } : positionRef.current,
                restoredLayoutRef.current ? viewportLimits() : sizeRef.current
            );
        };
        window.addEventListener('resize', handleViewportResize);
        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', handleViewportResize);
        };
    }, [applyLayout, endPointerInteraction, open]);

    useEffect(() => {
        if (!open || (!isDragging && !isResizing)) return undefined;
        const handlePointerMove = (event) => {
            const dragState = dragStateRef.current;
            const resizeState = resizeStateRef.current;
            const gesture = dragState || resizeState;
            if (!gesture || (gesture.pointerId != null && event.pointerId != null && gesture.pointerId !== event.pointerId)) return;
            if (dragState) {
                applyLayout({ x: event.clientX - dragState.offsetX, y: event.clientY - dragState.offsetY }, sizeRef.current);
                return;
            }
            const limits = viewportLimits();
            const dx = event.clientX - resizeState.startX;
            const dy = event.clientY - resizeState.startY;
            let { x, y, width, height } = resizeState;
            if (resizeState.edge.includes('w')) {
                const right = x + width;
                x = clamp(x + dx, VIEWPORT_PADDING, right - limits.minWidth);
                width = right - x;
            }
            if (resizeState.edge.includes('e')) width = clamp(width + dx, limits.minWidth, window.innerWidth - x - VIEWPORT_PADDING);
            if (resizeState.edge.includes('s')) height = clamp(height + dy, limits.minHeight, window.innerHeight - y - VIEWPORT_PADDING);
            applyLayout({ x, y }, { width, height });
        };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', endPointerInteraction);
        window.addEventListener('pointercancel', endPointerInteraction);
        window.addEventListener('blur', endPointerInteraction);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', endPointerInteraction);
            window.removeEventListener('pointercancel', endPointerInteraction);
            window.removeEventListener('blur', endPointerInteraction);
        };
    }, [applyLayout, endPointerInteraction, isDragging, isResizing, open]);

    useEffect(() => {
        if (!open) return;
        setActiveTab(TAB_SECURITY_RETURNS);
    }, [open]);

    const handleDragStart = useCallback((event) => {
        if ((event.button !== undefined && event.button !== 0) || isResizing || expanded) return;
        const panelEl = panelRef.current;
        if (!panelEl) return;
        const rect = panelEl.getBoundingClientRect();
        dragStateRef.current = { pointerId: event.pointerId, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top };
        setIsDragging(true);
        event.preventDefault();
    }, [expanded, isResizing]);

    const handleResizeStart = useCallback((event, edge = 'se') => {
        if (event.button !== undefined && event.button !== 0) return;
        event.stopPropagation();
        dragStateRef.current = null;
        restoredLayoutRef.current = null;
        setExpanded(false);
        setIsDragging(false);
        resizeStateRef.current = {
            pointerId: event.pointerId, edge,
            startX: event.clientX, startY: event.clientY,
            ...positionRef.current, ...sizeRef.current,
        };
        setIsResizing(true);
        event.preventDefault();
    }, []);

    const handleResizeKeyDown = useCallback((event, edge = 'se') => {
        const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        if (!directions[event.key]) return;
        event.preventDefault();
        const [dx, dy] = directions[event.key];
        const step = event.shiftKey ? 64 : 16;
        restoredLayoutRef.current = null;
        setExpanded(false);
        const position = { ...positionRef.current };
        let width = sizeRef.current.width + (dx * step);
        if (edge.includes('w')) {
            const right = position.x + sizeRef.current.width;
            position.x = clamp(position.x + (dx * step), VIEWPORT_PADDING, right - viewportLimits().minWidth);
            width = right - position.x;
        }
        applyLayout(position, { width, height: sizeRef.current.height + (dy * step) });
    }, [applyLayout]);

    const toggleExpanded = useCallback(() => {
        endPointerInteraction();
        if (restoredLayoutRef.current) {
            const previous = restoredLayoutRef.current;
            restoredLayoutRef.current = null;
            setExpanded(false);
            applyLayout(previous.position, previous.size);
        } else {
            restoredLayoutRef.current = { position: { ...positionRef.current }, size: { ...sizeRef.current } };
            setExpanded(true);
            applyLayout({ x: VIEWPORT_PADDING, y: VIEWPORT_PADDING }, viewportLimits());
        }
    }, [applyLayout, endPointerInteraction]);

    const dates = Array.isArray(securityBreakout?.dates) ? securityBreakout.dates : [];
    const latestDate = dates.length ? dates[dates.length - 1] : null;
    const selectedDate = String(selectedTimestamp || '').trim() || null;
    const selectedRangeStartRaw = String(selectedRangeStart || '').trim() || null;
    const selectedRangeEndRaw = String(selectedRangeEnd || '').trim() || null;
    const effectiveDate = useMemo(() => {
        if (!dates.length) return null;
        if (!selectedDate) return latestDate;
        if (dates.includes(selectedDate)) return selectedDate;
        let fallback = null;
        for (const dateValue of dates) {
            if (dateValue <= selectedDate) {
                fallback = dateValue;
                continue;
            }
            break;
        }
        return fallback || latestDate;
    }, [dates, latestDate, selectedDate]);
    const effectiveRange = useMemo(() => {
        if (!dates.length || !selectedRangeStartRaw || !selectedRangeEndRaw) return null;
        const resolveAtOrBefore = (target, fallback) => {
            if (dates.includes(target)) return target;
            let resolved = null;
            for (const dateValue of dates) {
                if (dateValue <= target) {
                    resolved = dateValue;
                    continue;
                }
                break;
            }
            return resolved || fallback;
        };
        let rangeStart = resolveAtOrBefore(selectedRangeStartRaw, dates[0]);
        let rangeEnd = resolveAtOrBefore(selectedRangeEndRaw, latestDate || dates[dates.length - 1]);
        if (!rangeStart || !rangeEnd) return null;
        if (rangeStart > rangeEnd) {
            const temp = rangeStart;
            rangeStart = rangeEnd;
            rangeEnd = temp;
        }
        return { start: rangeStart, end: rangeEnd };
    }, [dates, latestDate, selectedRangeEndRaw, selectedRangeStartRaw]);
    const hasExplicitRange = Boolean(
        effectiveRange?.start
        && effectiveRange?.end
        && effectiveRange.start !== effectiveRange.end
    );
    const sourceSeriesKey = useMemo(() => {
        const accumulation = seriesMode === 'absolute' ? 'absolute' : 'cumulative';
        const scale = valueMode === 'percent' ? 'pct' : 'dollar';
        return `${accumulation}_${scale}`;
    }, [seriesMode, valueMode]);
    const componentColumns = useMemo(() => {
        const items = Array.isArray(securityBreakout?.items) ? securityBreakout.items : [];
        const basisKey = valueMode === 'percent' ? 'security_return' : 'contribution';
        const hasFactor = items.some((item) => item?.[basisKey]?.factor);
        const hasIdio = items.some((item) => item?.[basisKey]?.idio);
        return CORE_COMPONENT_COLUMNS.filter((column) => {
            if (executionContext && ['factor', 'idio'].includes(column.key)) return false;
            if (column.key === 'factor') return hasFactor;
            if (column.key === 'idio') return hasIdio;
            return true;
        });
    }, [securityBreakout, valueMode, executionContext]);

    const rows = useMemo(() => {
        const items = Array.isArray(securityBreakout?.items) ? securityBreakout.items : [];
        if (!effectiveDate || !items.length) return [];
        const basisKey = valueMode === 'percent' ? 'security_return' : 'contribution';
        const scaleKey = valueMode === 'percent' ? 'pct' : 'dollar';
        const rangeStartIndex = hasExplicitRange ? dates.indexOf(effectiveRange.start) : -1;
        const rangeEndIndex = hasExplicitRange ? dates.indexOf(effectiveRange.end) : -1;
        const rangeStart = hasExplicitRange ? Math.max(0, Math.min(rangeStartIndex, rangeEndIndex)) : -1;
        const rangeEnd = hasExplicitRange ? Math.max(rangeStartIndex, rangeEndIndex) : -1;
        const startBaseIndex = rangeStart > 0 ? rangeStart - 1 : -1;

        return items.map((item) => {
            const basis = item?.[basisKey] || {};

            const values = {};
            for (const column of componentColumns) {
                if (hasExplicitRange && rangeStart >= 0 && rangeEnd >= 0) {
                    if (seriesMode === 'cumulative') {
                        const cumulativeSeries = basis?.[column.key]?.[`cumulative_${scaleKey}`] || {};
                        const endValue = Number(cumulativeSeries?.[effectiveRange.end] ?? 0);
                        const startBaseDate = startBaseIndex >= 0 ? dates[startBaseIndex] : null;
                        const startBaseValue = startBaseDate
                            ? Number(cumulativeSeries?.[startBaseDate] ?? 0)
                            : 0;
                        values[column.key] = endValue - startBaseValue;
                    } else {
                        const absoluteSeries = basis?.[column.key]?.[`absolute_${scaleKey}`] || {};
                        let windowSum = 0;
                        for (let idx = rangeStart; idx <= rangeEnd; idx += 1) {
                            windowSum += Number(absoluteSeries?.[dates[idx]] ?? 0);
                        }
                        values[column.key] = windowSum;
                    }
                } else {
                    values[column.key] = Number(
                        basis?.[column.key]?.[sourceSeriesKey]?.[effectiveDate] ?? 0
                    );
                }
            }

            const rawCoverageReasons = item?.coverage_reasons ?? item?.coverageReasons;
            const coverageReasons = Array.isArray(rawCoverageReasons)
                ? rawCoverageReasons
                : (rawCoverageReasons ? [String(rawCoverageReasons)] : []);
            const identity = identityFor(item);
            return {
                instrumentKey: item?.instrument_key || '',
                displayName: identity.value,
                identityTooltip: identity.tooltip,
                values,
                coverageReasons,
                hasPartialCoverage: Boolean(item?.has_partial_coverage) || coverageReasons.length > 0,
            };
        });
    }, [
        dates,
        effectiveDate,
        effectiveRange,
        hasExplicitRange,
        componentColumns,
        securityBreakout,
        seriesMode,
        sourceSeriesKey,
        valueMode,
        identityFor,
    ]);

    // In name order, so rows tied on a value stay in name order whichever
    // way the column sorts.
    const nameOrderedRows = useMemo(
        () => [...rows].sort((a, b) => a.displayName.localeCompare(b.displayName)),
        [rows]
    );

    const maxAbsByColumn = useMemo(() => {
        const out = {};
        for (const column of componentColumns) {
            let maxAbs = 0;
            for (const row of rows) {
                maxAbs = Math.max(maxAbs, Math.abs(Number(row.values[column.key] || 0)));
            }
            out[column.key] = maxAbs;
        }
        return out;
    }, [componentColumns, rows]);
    const totalSummary = useMemo(() => {
        const values = {};
        for (const column of componentColumns) {
            values[column.key] = rows.reduce(
                (acc, row) => acc + Number(row?.values?.[column.key] || 0),
                0
            );
        }
        return { top: [{ total: true, displayName: 'Total', values }] };
    }, [componentColumns, rows]);
    const summaryMetricItems = useMemo(() => ([
        {
            key: 'total_sharpe',
            label: 'Sharpe',
            value: formatSummaryMetric('total_sharpe', summaryMetrics?.total_sharpe),
        },
        {
            key: 'specific_sharpe',
            label: 'Spec. Sharpe',
            value: formatSummaryMetric('specific_sharpe', summaryMetrics?.specific_sharpe),
        },
        {
            key: 'hit_rate',
            label: 'Hit Rate',
            value: formatSummaryMetric('hit_rate', summaryMetrics?.hit_rate),
        },
    ]), [
        summaryMetrics?.hit_rate,
        summaryMetrics?.specific_sharpe,
        summaryMetrics?.total_sharpe,
    ]);

    const hasExecutionContext = Boolean(executionContext);
    const recentExecutionRows = useMemo(
        () => (Array.isArray(executionContext?.recentExecutions) ? executionContext.recentExecutions : []),
        [executionContext]
    );
    const noExecutionsMessage = String(
        executionContext?.noExecutionsMessage || 'No trades found for the selected point.'
    );

    const handleOpenCoveragePopover = useCallback((event, row) => {
        event.preventDefault();
        event.stopPropagation();
        setCoveragePopover({ anchorEl: event.currentTarget, row });
    }, []);

    const breakoutColumns = useMemo(() => [
        {
            id: 'security',
            header: securityIdentityFieldLabel(identityField),
            accessorFn: (row) => row.displayName,
            sortFn: sortByText,
            size: BREAKOUT_IDENTITY_WIDTH,
            cell: ({ row: { original } }) => (original.total ? original.displayName : (
                <span className="flex min-w-0 items-center gap-1">
                    <KitTooltip content={original.identityTooltip}>
                        <span className={cx('min-w-0 truncate', identityField === 'submitted_identifier' && 'font-mono')}>
                            {original.displayName}
                        </span>
                    </KitTooltip>
                    {original.hasPartialCoverage && (
                        <KitTooltip content="View missingness details">
                            <KitIconButton
                                size="xs"
                                label="View missingness details"
                                className="!text-ui-warning"
                                // The panel is draggable: a press here must not start a drag.
                                onPointerDown={(event) => event.stopPropagation()}
                                onClick={(event) => handleOpenCoveragePopover(event, original)}
                            >
                                <AlertTriangle aria-hidden="true" size={14} strokeWidth={2.25} />
                            </KitIconButton>
                        </KitTooltip>
                    )}
                </span>
            )),
            meta: { cellClassName: breakoutIdentityClassName, headerControls: identityHeaderControls },
        },
        ...componentColumns.map((column) => ({
            id: column.key,
            header: column.label,
            // Missing values count as zero.
            accessorFn: (row) => normalizeSortValue(row.values[column.key]),
            sortFn: sortByNumber,
            sortDescFirst: true,
            minSize: 130,
            cell: ({ row: { original } }) => formatCellValue(original.values[column.key], valueMode),
            meta: {
                grow: true,
                cellClassName: breakoutCellClassName,
                // Each column's colours scale to its own largest magnitude.
                cellStyle: ({ row: { original } }) => ({
                    backgroundColor: resolveHeatColor(original.values[column.key], maxAbsByColumn[column.key]),
                }),
            },
        })),
    ], [identityField, identityHeaderControls, componentColumns, valueMode, maxAbsByColumn, handleOpenCoveragePopover]);

    const isTradeHighlighted = useCallback((row) => (hasExplicitRange
        ? Boolean(
            row?.bucket_timestamp
            && row.bucket_timestamp >= effectiveRange.start
            && row.bucket_timestamp <= effectiveRange.end
        )
        : Boolean(
            executionContext?.selectedTimestamp
            && row?.bucket_timestamp === executionContext.selectedTimestamp
        )), [hasExplicitRange, effectiveRange, executionContext]);
    // Trades in the selected range (or at the selected point) are tinted and
    // marked with data-in-range.
    const getTradeRowProps = useCallback(
        (row) => (isTradeHighlighted(row) ? IN_RANGE_TRADE_ROW_PROPS : undefined),
        [isTradeHighlighted]
    );
    const tradeColumns = useMemo(() => [
        {
            id: 'tradedAt',
            header: 'Traded At',
            accessorFn: (row) => String(row?.executed_at_label || '-'),
            size: 132,
        },
        {
            id: 'security',
            header: securityIdentityFieldLabel(identityField),
            minSize: 190,
            cell: ({ row: { original } }) => {
                const identity = identityFor(original);
                return (
                    <KitTooltip content={identity.tooltip}>
                        <span className={cx('font-semibold text-content-primary', identityField === 'submitted_identifier' && 'font-mono')}>
                            {identity.value}
                        </span>
                    </KitTooltip>
                );
            },
            meta: { grow: true, headerControls: identityHeaderControls },
        },
        { id: 'side', header: 'Side', accessorFn: (row) => String(row?.side || '-'), size: 50 },
        {
            id: 'quantity',
            header: 'Qty',
            accessorFn: (row) => (row?.quantity == null
                ? '-'
                : Number(row.quantity).toLocaleString(undefined, { maximumFractionDigits: 4 })),
            size: 84,
        },
        { id: 'price', header: 'Price', accessorFn: (row) => formatCellValue(row?.trade_price, 'dollar'), size: 90 },
    ].map((column) => ({
        ...column,
        enableSorting: false,
        meta: { cellClassName: 'text-content-secondary', ...column.meta },
    })), [identityField, identityFor, identityHeaderControls]);

    const handleCloseCoveragePopover = useCallback(() => {
        setCoveragePopover({ anchorEl: null, row: null });
    }, []);

    if (!open) return null;

    const isIntradayTimeline = Boolean(
        dates.some((value) => String(value || '').includes('T'))
        || String(selectedTimestamp || '').includes('T')
        || String(selectedRangeStartRaw || '').includes('T')
        || String(selectedRangeEndRaw || '').includes('T')
    );
    const displayWindowStartRaw = (attributionMode ? windowStartDate : null) || (isIntradayTimeline
        ? ((hasExplicitRange ? effectiveRange.start : null) || dates[0] || selectedTimestamp || windowStartDate || asOfDate || '—')
        : ((hasExplicitRange ? effectiveRange.start : null) || windowStartDate || dates[0] || asOfDate || '—'));
    const displayWindowStart = isIntradayTimeline
        ? (formatTimeOnly(displayWindowStartRaw) || String(displayWindowStartRaw))
        : (normalizeDateOnly(displayWindowStartRaw) || String(displayWindowStartRaw));
    const clickedDate = effectiveDate || normalizeDateOnly(selectedTimestamp);
    const displayWindowEndRaw = (attributionMode ? windowEndDate : null) || (isIntradayTimeline
        ? ((hasExplicitRange ? effectiveRange.end : null) || effectiveDate || selectedTimestamp || latestDate || windowEndDate || asOfDate || displayWindowStartRaw || '—')
        : ((hasExplicitRange ? effectiveRange.end : null) || clickedDate || windowEndDate || asOfDate || latestDate || displayWindowStartRaw || '—'));
    const displayWindowEnd = isIntradayTimeline
        ? (formatTimeOnly(displayWindowEndRaw) || String(displayWindowEndRaw))
        : (normalizeDateOnly(displayWindowEndRaw) || String(displayWindowEndRaw));
    const displayGroup = String(groupLabel || '').trim() || 'Portfolio';
    const rangeLabel = isIntradayTimeline ? 'Time' : 'Window';
    const exposureOnly = attributionAnalysisBasis === 'selected_snapshot_exposures_only';
    const contributorScope = exposureOnly ? (selectedFactors.length ? `Snapshot exposures | Factors: ${selectedFactors.join(', ')}` : 'Snapshot exposures') : selectedFactors.length === 1 ? `${selectedFactors[0]} contributors`
        : selectedFactors.length ? `Selected-factor contributors | Factors: ${selectedFactors.join(', ')}` : 'All-factor contributors';
    const popoutTitle = `${attributionMode ? contributorScope : 'Security Returns'} | ${displayGroup} | ${rangeLabel}: ${displayWindowStart} -> ${displayWindowEnd}`;
    const coveragePopoverOpen = Boolean(coveragePopover.anchorEl && coveragePopover.row);
    const coverageReasons = Array.isArray(coveragePopover.row?.coverageReasons)
        ? coveragePopover.row.coverageReasons
        : [];

    return (
        <Portal>
            <Paper
                ref={panelRef}
                role="dialog"
                aria-label={popoutTitle}
                elevation={8}
                sx={{
                    ...ELEVATED_CARD_SX,
                    position: 'fixed',
                    top: position.y,
                    left: position.x,
                    zIndex: (theme) => theme.zIndex.modal + 5,
                    width: size.width,
                    height: size.height,
                    overflow: 'hidden',
                    bgcolor: '#000',
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    ...performancePopoverPaperSx,
                }}
            >
                <Box
                    onPointerDown={handleDragStart}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                        borderBottom: '1px solid #39424d',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        borderColor: (theme) => (usesAppStyleGrammar(theme)
                            ? MSI_PALETTE.line
                            : '#39424d'),
                        bgcolor: (theme) => (usesAppStyleGrammar(theme)
                            ? MSI_COMPONENT_COLORS.tableHeader
                            : 'rgba(255,255,255,0.06)'),
                        cursor: expanded ? 'default' : isDragging ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <DragIndicatorRoundedIcon fontSize="small" />
                        <Tooltip title={popoutTitle} arrow><Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                            {popoutTitle}
                        </Typography></Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.5 }}>
                        <Tooltip title={expanded ? 'Restore size' : 'Expand window'} arrow>
                            <IconButton size="small" onPointerDown={(event) => event.stopPropagation()} onClick={toggleExpanded}
                                aria-label={expanded ? 'Restore contributors inspector size' : 'Expand contributors inspector'} sx={{ color: 'text.secondary' }}>
                                {expanded ? <CloseFullscreenRoundedIcon fontSize="small" /> : <OpenInFullRoundedIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={onClose}
                            aria-label="Close contributors inspector"
                            sx={{ color: 'text.secondary' }}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{ p: 1.2, pb: 3.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0, minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                    {attributionMode ? <AttributionDetails decompositionMode={attributionDecompositionMode} analysisSource={analysisSource} analysisBasis={analysisBasis} factorDetailFocus={factorDetailFocus} identityRows={identityRows} identityDisplayField={identityDisplayField} onIdentityDisplayFieldChange={onIdentityDisplayFieldChange} payload={attributionDetail} loading={loading} error={attributionDetailError} currency={reportingCurrency} selectedFactors={selectedFactors} startDate={attributionDetail?.security_contributions?.window?.start_date || windowStartDate || selectedRangeStart} endDate={attributionDetail?.security_contributions?.window?.end_date || windowEndDate || selectedRangeEnd || selectedTimestamp} /> : <>
                    <Tabs
                        value={activeTab}
                        onChange={(_, next) => setActiveTab(next)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 34,
                            borderBottom: '1px solid #2b3440',
                            borderColor: (theme) => (usesAppStyleGrammar(theme)
                                ? MSI_PALETTE.line
                                : '#2b3440'),
                            '& .MuiTab-root': {
                                minHeight: 34,
                                py: 0.45,
                                color: (theme) => (usesAppStyleGrammar(theme)
                                    ? MSI_PALETTE.ink500
                                    : '#cbd5e1'),
                                textTransform: 'none',
                                fontSize: 12,
                                letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
                            },
                            '& .Mui-selected': {
                                color: (theme) => (usesAppStyleGrammar(theme)
                                    ? MSI_PALETTE.ink900
                                    : '#fff'),
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: (theme) => (usesAppStyleGrammar(theme)
                                    ? MSI_COMPONENT_COLORS.focusBorder
                                    : '#60a5fa'),
                            },
                        }}
                    >
                        <Tab value={TAB_SECURITY_RETURNS} label="Security Returns" />
                        <Tab
                            value={TAB_RECENT_TRADES}
                            label="Recent Trades"
                            disabled={!hasExecutionContext}
                        />
                    </Tabs>

                    {activeTab === TAB_SECURITY_RETURNS ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <ToggleButtonGroup
                                    value={valueMode}
                                    exclusive
                                    onChange={(_, value) => value && setValueMode(value)}
                                    size="small"
                                    sx={{ border: '1px solid #555', bgcolor: '#000', height: 30, ...performanceToggleGroupSx }}
                                >
                                    <ToggleButton value="dollar" sx={{ color: '#fff', px: 1.35, ...performanceToggleButtonSx }}>$</ToggleButton>
                                    <ToggleButton value="percent" sx={{ color: '#fff', px: 1.35, ...performanceToggleButtonSx }}>%</ToggleButton>
                                </ToggleButtonGroup>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65, flexWrap: 'wrap' }}>
                                    {summaryMetricItems.map((metric) => (
                                        <Box
                                            key={metric.key}
                                            sx={{
                                                px: 0.8,
                                                py: 0.32,
                                                borderRadius: 0.75,
                                                border: '1px solid rgba(148, 163, 184, 0.5)',
                                                bgcolor: 'rgba(15, 23, 42, 0.38)',
                                                lineHeight: 1,
                                                whiteSpace: 'nowrap',
                                                ...performanceInsetSx,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                {`${metric.label}: `}
                                                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                                    {metric.value}
                                                </Box>
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2">Loading security breakout...</Typography>
                                </Box>
                            ) : !rows.length ? (
                                <Alert severity="info" variant="outlined">
                                    No security breakout data available for this view.
                                </Alert>
                            ) : (
                                <DataTable
                                    aria-label="Security returns"
                                    data={nameOrderedRows}
                                    columns={breakoutColumns}
                                    getRowId={getBreakoutRowId}
                                    sorting={sorting}
                                    onSortingChange={setSorting}
                                    enableSortingRemoval={false}
                                    summaryRows={totalSummary}
                                    striped={false}
                                    className="min-h-0 flex-1"
                                    virtualize
                                />
                            )}
                        </>
                    ) : activeTab === TAB_RECENT_TRADES ? (
                        <DataTable
                            aria-label="Recent trades"
                            data={recentExecutionRows}
                            columns={tradeColumns}
                            getRowId={getTradeRowId}
                            getRowProps={getTradeRowProps}
                            emptyMessage={noExecutionsMessage}
                            className="min-h-0 flex-1"
                        />
                    ) : (
                        <Alert severity="info" variant="outlined">
                            Select a tab to view data.
                        </Alert>
                    )}
                    </>}
                </Box>

                {[
                    { edge: 'w', label: 'Resize left edge', sx: { left: 0, top: 48, bottom: 28, width: 6, cursor: 'ew-resize' } },
                    { edge: 'e', label: 'Resize right edge', sx: { right: 0, top: 48, bottom: 28, width: 6, cursor: 'ew-resize' } },
                    { edge: 's', label: 'Resize bottom edge', sx: { bottom: 0, left: 28, right: 28, height: 6, cursor: 'ns-resize' } },
                ].map(({ edge, label, sx }) => <Box key={edge} aria-label={label} onPointerDown={(event) => handleResizeStart(event, edge)}
                    sx={{ position: 'absolute', touchAction: 'none', ...sx }} />)}
                {[
                    { edge: 'sw', label: 'Resize contributors inspector from bottom left', side: 'left', cursor: 'nesw-resize', Icon: SouthWestRoundedIcon },
                    { edge: 'se', label: 'Resize contributors inspector', side: 'right', cursor: 'nwse-resize', Icon: SouthEastRoundedIcon },
                ].map(({ edge, label, side, cursor, Icon }) => <Tooltip key={edge} title="Drag to resize. Arrow keys adjust size; Shift makes larger steps." arrow>
                    <IconButton size="small" aria-label={label} onPointerDown={(event) => handleResizeStart(event, edge)}
                        onKeyDown={(event) => handleResizeKeyDown(event, edge)}
                        sx={{ position: 'absolute', [side]: 2, bottom: 2, width: 26, height: 26, p: 0.35, borderRadius: 0.6,
                            cursor, touchAction: 'none', color: 'text.secondary',
                            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                            '&:hover, &:focus-visible': { color: 'primary.main', borderColor: 'primary.main' } }}>
                        <Icon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>)}
            </Paper>

            <Popover
                open={coveragePopoverOpen}
                anchorEl={coveragePopover.anchorEl}
                onClose={handleCloseCoveragePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 30,
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.6,
                            maxWidth: 420,
                            bgcolor: '#000',
                            color: '#e5e7eb',
                            border: '1px solid rgba(148, 163, 184, 0.55)',
                            ...performancePopoverPaperSx,
                        },
                    },
                }}
            >
                <Box sx={{ p: 1.2, display: 'grid', gap: 0.55 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {coveragePopover.row?.displayName || 'Coverage Details'}
                    </Typography>
                    {coverageReasons.length ? (
                        coverageReasons.map((reason) => (
                            <Typography
                                key={`${coveragePopover.row?.instrumentKey || 'unknown'}-${reason}`}
                                variant="body2"
                                sx={{ fontSize: 12.5, color: 'text.primary' }}
                            >
                                {`- ${formatCoverageReasonDetail(reason)}`}
                            </Typography>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ fontSize: 12.5, color: 'text.primary' }}>
                            Coverage details are unavailable for this security.
                        </Typography>
                    )}
                </Box>
            </Popover>
        </Portal>
    );
}
